import { createFileRoute } from '@tanstack/react-router'

import { JellyIcon } from '@/components/icons'
import { SearchField } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import { ClientRow, ClientTile } from '@/components/spravce/klienti'
import { CLIENTS } from '@/mocks/spravce/klienti'

/** Which way she is looking at the directory — in the URL like every other state. */
type KlientiSearch = { stav?: 'seznam' }

const GRID_HREF = '/spravce/klienti'
const LIST_HREF = '/spravce/klienti?stav=seznam'

export const Route = createFileRoute('/spravce/klienti/')({
  validateSearch: (search: Record<string, unknown>): KlientiSearch =>
    search.stav === 'seznam' ? { stav: 'seznam' } : {},
  head: () => ({ meta: [{ title: 'Klienti · Aplikace Bitez' }] }),
  component: SpravceKlienti,
})

/** Two segments in one pill — the app's chip family doing a switch's job. */
function ViewSwitch({ list }: { list: boolean }) {
  return (
    <div className="flex shrink-0 items-center gap-1 rounded-full bg-white p-1 shadow-sm ring-1 ring-ink/5">
      {[
        { href: GRID_HREF, icon: 'grid' as const, label: 'Dlaždice', active: !list },
        { href: LIST_HREF, icon: 'list' as const, label: 'Seznam', active: list },
      ].map((view) => (
        <Link
          key={view.label}
          href={view.href}
          aria-label={view.label}
          aria-current={view.active ? 'true' : undefined}
          className={`flex size-8 items-center justify-center rounded-full transition-colors duration-150 ${
            view.active ? 'bg-brand text-cream' : 'text-ink/40 hover:text-ink/70'
          }`}
        >
          <JellyIcon name={view.icon} variant={view.active ? 'fill' : 'line'} className="size-4" />
        </Link>
      ))}
    </div>
  )
}

/**
 * The directory — her third question, after "co teď" (Přehled) and "jak jde
 * výroba" (Plány): **kdo to je.**
 *
 * Two views of the same list, because the question changes with the number of
 * clients: a wall of venues is for recognising a place (she knows the room
 * before the name), a column of rows is for finding one by name once there are
 * twenty of them. State in the URL, so a view is linkable like everything else.
 *
 * The header holds **no title** — "Klienti" is already the tab, and titles that
 * repeat the tab bar were deleted on 2026-07-30. It holds the two things a
 * directory actually needs: a way to search it and a way to look at it.
 */
function SpravceKlienti() {
  const { stav } = Route.useSearch()
  const list = stav === 'seznam'

  return (
    <div className="px-4 pt-6 pb-6">
      <header className="flex items-center gap-2.5">
        <div className="min-w-0 flex-1">
          <SearchField placeholder="Hledat podnik" />
        </div>
        <ViewSwitch list={list} />
      </header>

      {list ? (
        <div className="mt-6 space-y-5">
          {CLIENTS.map((client, index) => (
            <div
              key={client.venue}
              className="animate-rise-up"
              style={{ animationDelay: `${index * 60}ms` }}
            >
              <ClientRow client={client} />
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-x-3 gap-y-6">
          {CLIENTS.map((client, index) => (
            <div
              key={client.venue}
              className="animate-rise-up"
              style={{ animationDelay: `${index * 60}ms` }}
            >
              <ClientTile client={client} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
